<template>
  <div :class="embedded ? 'speech-embedded' : 'speech-popover-wrapper'">
    <div v-if="!embedded" class="floating-button-container">
      <button
        @click="togglePopover"
        :class="['floating-btn', { 'listening': isListening, 'has-content': hasTranscript }]"
        :title="isListening ? 'Ouvindo... Clique para abrir' : 'Clique para configurar áudio'"
      >
        <div class="mic-icon">
          <svg v-if="!isListening" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M12 1c-1.7 0-3 1.3-3 3v8c0 1.7 1.3 3 3 3s3-1.3 3-3V4c0-1.7-1.3-3-3-3zm5.3 9c0 3-2.5 5.1-5.3 5.1S6.7 13 6.7 10H5c0 3.4 2.7 6.2 6 6.7V20h2v-3.3c3.3-.5 6-3.3 6-6.7h-1.7z"/>
          </svg>
          <div v-else class="listening-animation">
            <div class="sound-wave wave-1"></div>
            <div class="sound-wave wave-2"></div>
            <div class="sound-wave wave-3"></div>
          </div>
        </div>
        <span class="status-text">{{ statusText }}</span>
      </button>

    </div>

    <div
      v-if="embedded || showPopover"
      :class="embedded ? 'speech-embedded-body' : 'popover-overlay'"
      @click="embedded ? undefined : closePopover"
    >
      <div
        :class="[
          embedded ? 'speech-embedded-content' : 'popover-content',
          !embedded && hasTranscript ? 'w-100 w-lg-75 w-md-75' : '',
          !embedded && !hasTranscript ? 'w-50' : '',
        ]"
        @click.stop
      >
        <div v-if="!embedded" class="popover-header">
          <h3>{{ embedded ? 'Transcrição' : 'Reconhecimento de Voz' }}</h3>
          <div v-if="!embedded" class="header-controls">
            <button
              @click="closePopover"
              class="control-btn close-btn"
              title="Fechar"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>

        <div :class="embedded ? 'speech-embedded-inner' : 'popover-body'">
          <div class="status-indicator" :class="{ active: isListening }">
            <div class="pulse" v-if="isListening"></div>
            <span v-if="isPaused">{{ isListening ? 'Ouvindo...' : 'Pausado' }}</span>
            <span v-else>{{ isListening ? 'Ouvindo...' : 'Parado' }}</span>
          </div>

          <div class="controls">
            <button v-if="isPaused"
              @click="resumeListening"
              :disabled="isListening || !isSupported"
              class="btn btn-start"
            >
              {{ isSupported ? 'Continuar' : 'Não Suportado' }}
            </button>

            <button v-else
              @click="startListening"
              :disabled="isListening || !isSupported"
              class="btn btn-start"
            >
              {{ isSupported ? 'Iniciar' : 'Não Suportado' }}
            </button>

            <button
              @click="pauseListening"
              :disabled="!isListening"
              class="btn btn-clear"
            >
              Pausar
            </button>

            <button
              @click="stopListening"
              :disabled="!isListening"
              class="btn btn-stop"
            >
              Parar
            </button>
          </div>

          <p v-if="isListening && !liveSpeechSupported" class="live-preview-hint">
            Prévia ao vivo indisponível neste navegador. A transcrição completa será gerada ao parar.
          </p>

          <div class="transcript-area" v-if="showTranscriptArea">
            <div class="transcript-area-header">
              <h4>Transcrição</h4>
              <span v-if="isRefined" class="refined-badge">Refinada</span>
            </div>

            <div class="form-group" v-if="audioUrl">
              <audio ref="audioPlayer" :src="audioUrl" class="d-none" @timeupdate="onAudioTimeUpdate" @loadedmetadata="onAudioLoaded" @ended="onAudioEnded"></audio>
              <div class="custom-audio-player">
                <button type="button" class="custom-audio-btn" @click="toggleAudioPlayback">
                  <i :class="isAudioPlaying ? 'bi bi-pause-fill' : 'bi bi-play-fill'"></i>
                </button>
                <div class="custom-audio-track">
                  <input
                    type="range"
                    class="custom-audio-range"
                    min="0"
                    :max="audioDuration || 0"
                    step="0.1"
                    :value="audioCurrentTime"
                    @input="seekAudio"
                  >
                </div>
                <span class="custom-audio-time">{{ formatAudioTime(audioCurrentTime) }} / {{ formatAudioTime(audioDuration) }}</span>
              </div>
            </div>

            <div ref="liveChatScroll" class="live-transcript-chat">
              <div
                v-for="utterance in utterances"
                :key="utterance.id"
                class="utterance-row"
                :class="utterance.side"
              >
                <div class="utterance-bubble">{{ utterance.text }}</div>
              </div>

              <div v-if="interimText" class="utterance-row interim">
                <div class="utterance-bubble utterance-bubble--interim">{{ interimText }}</div>
              </div>

              <p v-if="isListening && utterances.length === 0 && !interimText" class="live-empty-hint">
                Fale normalmente — cada pausa na fala vira um novo trecho.
              </p>

              <p v-if="isTranscribing" class="refining-hint">
                Refinando transcrição...
              </p>
            </div>

            <div class="col-md-12 mt-4 text-end">
              <button
                class="btn btn-info"
                :disabled="!hasTranscript || isTranscribing || isListening"
                @click="processTranscript()"
              >
                <i class="bi bi-stars"></i> Aplicar ao prontuário
              </button>
            </div>
          </div>

          <div class="error-message" v-if="error">
            <p>{{ error }}</p>
          </div>

          <div v-if="!embedded" class="settings-section">
            <h4>Configurações:</h4>
            <div class="setting-item">
              <label>
                <input
                  type="checkbox"
                  v-model="hideWhenListening"
                >
                Ocultar durante captura
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AudioRecorder from "@/helpers/audioRecorder";
import {
  createSpeechRecognition,
  isSpeechRecognitionSupported,
  splitTranscriptIntoSegments,
  stripTranscriptHtml,
} from "@/helpers/browserSpeechRecognition";
import http from "@/helpers/http";
import Loader from "@/helpers/loader";

export default {
  name: 'SpeechPopover',

  props: {
    medicalRecords: Object,
    schedule: Object,
    embedded: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      audioRecorder: null,
      audioUrl: null,
      isListening: false,
      isPaused: false,
      isSupported: true,
      isTranscribing: false,
      finalTranscript: "",
      utterances: [],
      interimText: "",
      isRefined: false,
      liveSpeechSupported: isSpeechRecognitionSupported(),
      speechRecognition: null,
      error: null,

      // Popover state
      showPopover: false,
      hideWhenListening: false,

      // Animations
      audioContext: null,
      analyser: null,
      source: null,
      mediaStream: null,
      volumeLevel: 0,
      rafId: null,
      isAudioPlaying: false,
      audioCurrentTime: 0,
      audioDuration: 0,
    }
  },

  watch: {
    isListening(newVal) {
      this.emitTranscriptionState();
      if (newVal && this.hideWhenListening) {
        this.showPopover = false;
      }
    },
    finalTranscript() {
      this.emitTranscriptionState();
    },
  },

  computed: {
    hasTranscript() {
      return this.finalTranscript.trim().length > 0;
    },

    showTranscriptArea() {
      return this.isListening
        || this.isPaused
        || this.utterances.length > 0
        || this.interimText.length > 0
        || this.audioUrl
        || this.isTranscribing;
    },

    statusText() {
      if (!this.isSupported) return 'Não suportado'
      if (this.isListening) return 'Ouvindo'
      if (this.hasTranscript) return 'Texto capturado'
      return 'Inativo'
    }
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
    this.emitTranscriptionState();
  },

  beforeUnmount() {
    this.cleanup()
    document.removeEventListener('keydown', this.handleKeydown)
  },

  methods: {
    emitTranscriptionState() {
      this.$emit('transcription-state', {
        isListening: this.isListening,
        hasTranscript: this.hasTranscript,
      });
    },

    processTranscript() {
      const transcript = stripTranscriptHtml(this.finalTranscript);

      if (!transcript) {
        this.error = 'Nenhum texto capturado para processar.';
        return;
      }

      Loader.show('Processando transcrição...');

      this.error = '';
      http.post(`/professionals/${this.schedule?.id_prof}/patients/${this.schedule?.id_patient}/medical-records/ai/process-transcription`, {
        medical_records: this.medicalRecords,
        transcript,
      })
        .then(response => {
          this.$emit('processed-transcript', response.data);
          this.showPopover = false
        })
        .catch(error => {
          this.isTranscribing = false;
          this.error = error;
        }).finally(() => {
          Loader.close();
        });
    },

    async initRecorder() {
      this.isSupported = true
      const recorder = new AudioRecorder();

      recorder.onRecordingStart = () => {
        this.isListening = true
        this.error = null
      };

      recorder.onRecordingStop = () => {
        console.log('⏹️ Gravação parada!');
      };

      recorder.onRecordingComplete = (audioData) => {
        this.isTranscribing = true;
        this.interimText = '';

        console.log('✅ Gravação completa:', audioData);
        this.audioUrl = audioData.url;

        const formData = new FormData();
        const audioFile = new File([audioData.blob], 'recording.webm', { type: audioData.blob.type });
        formData.append('audio', audioFile);

        http.post('/ai/speech-to-text', formData)
          .then(response => {
            this.applyRefinedTranscript(response.data);
            this.isTranscribing = false;
          })
          .catch(error => {
            this.isTranscribing = false;
            this.syncFinalTranscriptFromUtterances();
            this.error = error;
          });
      };

      recorder.onRecordingError = (error) => {
        this.error = error;
        console.error('❌ Erro na gravação:', error);
      };

      this.audioRecorder = recorder;
      await this.audioRecorder.init();
    },

    async startListening() {
      await this.initRecorder()

      if (!this.audioRecorder || !this.isSupported) return;

      if (this.audioUrl) {
        const result = await Swal.fire({
          title: 'Deseja gravar novamente?',
          text: 'A gravação anterior será perdida.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, gravar',
          cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;
      }

      try {
        this.clearTranscript();
        this.audioRecorder.startRecording();
        this.startLiveSpeechRecognition();
        this.error = null;
      } catch (error) {
        this.error = 'Erro ao iniciar o reconhecimento';
      }
    },

    pauseListening() {
      this.isListening = false;
      this.isPaused = true;
      this.stopLiveSpeechRecognition();

      if (this.audioRecorder) {
        this.audioRecorder.pauseRecording();
      }
    },

    stopListening() {
      this.isListening = false;
      this.isPaused = false;
      this.stopLiveSpeechRecognition();

      if (this.audioRecorder) {
        this.audioRecorder.stopRecording();
      }
    },

    resumeListening() {
      this.isListening = true;
      this.isPaused = false;

      if (this.audioRecorder) {
        this.audioRecorder.resumeRecording();
      }

      this.startLiveSpeechRecognition();
    },

    clearTranscript() {
      this.finalTranscript = '';
      this.utterances = [];
      this.interimText = '';
      this.isRefined = false;
      this.audioUrl = null;
      this.isTranscribing = false;
      this.stopAudioPlayback();
      this.audioCurrentTime = 0;
      this.audioDuration = 0;
    },

    startLiveSpeechRecognition() {
      if (!this.liveSpeechSupported) {
        return;
      }

      this.stopLiveSpeechRecognition();

      const recognition = createSpeechRecognition();
      if (!recognition) {
        return;
      }

      this.speechRecognition = recognition;

      recognition.onresult = (event) => {
        let interim = '';

        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          const result = event.results[index];
          const text = result[0]?.transcript?.trim();

          if (!text) {
            continue;
          }

          if (result.isFinal) {
            this.pushUtterance(text);
          } else {
            interim += result[0].transcript;
          }
        }

        this.interimText = interim.trim();
        this.scrollLiveChatToBottom();
      };

      recognition.onerror = (event) => {
        if (event.error === 'no-speech' || event.error === 'aborted') {
          return;
        }

        console.warn('Speech recognition error:', event.error);
      };

      recognition.onend = () => {
        if (this.isListening && !this.isPaused && this.speechRecognition === recognition) {
          try {
            recognition.start();
          } catch (error) {
            console.warn('Speech recognition restart failed:', error);
          }
        }
      };

      try {
        recognition.start();
      } catch (error) {
        console.warn('Speech recognition start failed:', error);
      }
    },

    stopLiveSpeechRecognition() {
      if (!this.speechRecognition) {
        return;
      }

      this.speechRecognition.onresult = null;
      this.speechRecognition.onerror = null;
      this.speechRecognition.onend = null;

      try {
        this.speechRecognition.abort();
      } catch (error) {
        try {
          this.speechRecognition.stop();
        } catch (stopError) {
          console.warn('Speech recognition stop failed:', stopError);
        }
      }

      this.speechRecognition = null;
    },

    pushUtterance(text) {
      const side = this.utterances.length % 2 === 0 ? 'left' : 'right';

      this.utterances.push({
        id: `${Date.now()}-${this.utterances.length}`,
        text,
        side,
      });

      this.isRefined = false;
      this.syncFinalTranscriptFromUtterances();
      this.scrollLiveChatToBottom();
    },

    syncFinalTranscriptFromUtterances() {
      this.finalTranscript = this.utterances.map((utterance) => utterance.text).join('\n\n');
    },

    applyRefinedTranscript(rawTranscript) {
      const plainText = stripTranscriptHtml(rawTranscript);
      const segments = splitTranscriptIntoSegments(plainText);

      if (segments.length === 0) {
        this.finalTranscript = plainText;
        return;
      }

      this.utterances = segments.map((text, index) => ({
        id: `refined-${index}`,
        text,
        side: index % 2 === 0 ? 'left' : 'right',
      }));

      this.finalTranscript = plainText;
      this.isRefined = true;
      this.scrollLiveChatToBottom();
    },

    scrollLiveChatToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.liveChatScroll;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    togglePopover() {
      this.showPopover = !this.showPopover
    },

    closePopover() {
      this.showPopover = false
    },

    handleKeydown(event) {
      if (event.key === 'Escape' && this.showPopover) {
        this.closePopover()
      }
    },

    cleanup() {
      this.stopLiveSpeechRecognition();
      this.stopListening();
      if (this.audioRecorder) {
        this.audioRecorder.cleanup();
        this.audioRecorder = null;
      }
    },

    async startAnimation() {
      try {
        this.mediaStream = this.audioRecorder.stream;

        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.source = this.audioContext.createMediaStreamSource(this.mediaStream);

        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 256;

        const bufferLength = this.analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        this.source.connect(this.analyser);

        const updateVolume = () => {
          this.analyser.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / bufferLength;

          this.volumeLevel = Math.min(100, Math.floor((average / 256) * 100)); // 0–100%
          this.rafId = requestAnimationFrame(updateVolume);
        };

        updateVolume();
      } catch (err) {
        console.error('Erro ao acessar o microfone:', err);
      }
    },

    pauseAnimation() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    },

    stopAnimation() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.audioContext?.close();
    },

    toggleAudioPlayback() {
      const audio = this.$refs.audioPlayer;
      if (!audio) {
        return;
      }

      if (audio.paused) {
        audio.play();
        this.isAudioPlaying = true;
        return;
      }

      audio.pause();
      this.isAudioPlaying = false;
    },

    stopAudioPlayback() {
      const audio = this.$refs.audioPlayer;
      if (!audio) {
        this.isAudioPlaying = false;
        return;
      }

      audio.pause();
      audio.currentTime = 0;
      this.isAudioPlaying = false;
    },

    seekAudio(event) {
      const audio = this.$refs.audioPlayer;
      if (!audio) {
        return;
      }

      audio.currentTime = Number(event.target.value);
      this.audioCurrentTime = audio.currentTime;
    },

    onAudioTimeUpdate() {
      const audio = this.$refs.audioPlayer;
      if (!audio) {
        return;
      }

      this.audioCurrentTime = audio.currentTime;
    },

    onAudioLoaded() {
      const audio = this.$refs.audioPlayer;
      if (!audio) {
        return;
      }

      this.audioDuration = audio.duration || 0;
    },

    onAudioEnded() {
      this.isAudioPlaying = false;
      this.audioCurrentTime = 0;
    },

    formatAudioTime(seconds) {
      if (!seconds || Number.isNaN(seconds)) {
        return '0:00';
      }

      const total = Math.floor(seconds);
      const mins = Math.floor(total / 60);
      const secs = String(total % 60).padStart(2, '0');

      return `${mins}:${secs}`;
    },
  }
}
</script>

<style scoped>
.speech-popover-wrapper {
  position: relative
}

/* Floating Button */
.floating-button-container {
  position: fixed;
  bottom: 20px;
  right: 90px;
  z-index: 1000;
}

.floating-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #2196F3;
  color: white;
  border: none;
  border-radius: 25px;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.floating-btn:hover {
  background: #1976D2;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(33, 150, 243, 0.4);
}

.floating-btn.listening {
  background: #4CAF50;
  animation: pulse-glow 2s infinite;
}

.floating-btn.has-content {
  background: #FF9800;
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }
  50% {
    box-shadow: 0 4px 20px rgba(76, 175, 80, 0.6);
  }
}

/* Listening Animation */
.listening-animation {
  display: flex;
  align-items: center;
  gap: 2px;
}

.sound-wave {
  width: 3px;
  height: 12px;
  background: currentColor;
  border-radius: 1px;
  animation: wave 1.5s infinite ease-in-out;
}

.wave-1 {
  animation-delay: 0s;
}

.wave-2 {
  animation-delay: 0.2s;
}

.wave-3 {
  animation-delay: 0.4s;
}

@keyframes wave {
  0%, 40%, 100% {
    transform: scaleY(0.4);
  }
  20% {
    transform: scaleY(1);
  }
}

/* Popover Overlay */
.popover-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Popover Content */
.popover-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  max-height: 80vh;
  overflow: hidden;
  transition: all 0.3s ease;
}

/* Popover Header */
.popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
}

.popover-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.header-controls {
  display: flex;
  gap: 8px;
}

.control-btn {
  padding: 6px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #666;
  transition: all 0.2s ease;
}

.control-btn:hover {
  background: #e9ecef;
  color: #333;
}

/* Popover Body */
.popover-body {
  padding: 20px;
  max-height: calc(80vh - 60px);
  overflow-y: auto;
}

/* Status Indicator */
.status-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: 8px;
  background: #f5f5f5;
  margin-bottom: 16px;
  font-size: 14px;
}

.status-indicator.active {
  background: #e8f5e8;
  color: #2d6a2d;
}

.pulse {
  width: 8px;
  height: 8px;
  background: #ff4444;
  border-radius: 50%;
  animation: pulse-dot 1.5s infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.3);
    opacity: 0.7;
  }
}

/* Controls */
.controls {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 80px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-start {
  background: #4CAF50;
  color: white;
}

.btn-start:hover:not(:disabled) {
  background: #45a049;
}

.btn-stop {
  background: #f44336;
  color: white;
}

.btn-stop:hover:not(:disabled) {
  background: #da190b;
}

.btn-clear {
  background: #9E9E9E;
  color: white;
}

.btn-clear:hover:not(:disabled) {
  background: #757575;
}

/* Transcript Area */
.transcript-area {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.transcript-area h4 {
  margin: 0;
  font-size: 14px;
  color: #333;
}

.transcript-area-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 12px;
}

.refined-badge {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #780f5a;
  background: rgba(120, 15, 90, 0.08);
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
}

.live-preview-hint,
.live-empty-hint,
.refining-hint {
  margin: 0;
  font-size: 0.8rem;
  color: #7e8299;
  text-align: center;
}

.live-transcript-chat {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e4e6ef;
  border-radius: 0.75rem;
  padding: 0.75rem;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.utterance-row {
  display: flex;
  width: 100%;
}

.utterance-row.left {
  justify-content: flex-start;
}

.utterance-row.right {
  justify-content: flex-end;
}

.utterance-row.interim {
  justify-content: flex-start;
}

.utterance-bubble {
  max-width: 85%;
  padding: 0.55rem 0.75rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.45;
  color: #181c32;
  background: #f1f3f8;
  word-break: break-word;
}

.utterance-row.right .utterance-bubble {
  background: rgba(120, 15, 90, 0.1);
  border-bottom-right-radius: 0.2rem;
}

.utterance-row.left .utterance-bubble {
  border-bottom-left-radius: 0.2rem;
}

.utterance-bubble--interim {
  color: #7e8299;
  font-style: italic;
  background: #fafafa;
  border: 1px dashed #e4e6ef;
}

.final-transcript {
  color: #333;
  margin: 0 0 8px 0;
}

.interim-transcript {
  color: #666;
  font-style: italic;
  margin: 0;
}

/* Error Message */
.error-message {
  background: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;
  padding: 12px;
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 16px;
}

/* Settings */
.settings-section {
  border-top: 1px solid #eee;
  padding-top: 16px;
  margin-top: 16px;
}

.settings-section h4 {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #333;
}

.setting-item {
  margin-bottom: 8px;
}

.setting-item label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #555;
  cursor: pointer;
}

.setting-item input[type="checkbox"] {
  margin: 0;
}

.speech-embedded-body {
  position: static;
  background: transparent;
  display: block;
}

.speech-embedded-content {
  background: transparent;
  box-shadow: none;
  max-height: none;
  overflow: visible;
}

.speech-embedded-inner {
  padding: 0;
  max-height: none;
  overflow: visible;
}

.custom-audio-player {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid #e4e6ef;
  border-radius: 0.5rem;
  background: #fff;
}

.custom-audio-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #780f5a 0%, #9d1b6c 100%);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.custom-audio-track {
  flex: 1;
  min-width: 0;
}

.custom-audio-range {
  width: 100%;
  accent-color: #780f5a;
}

.custom-audio-time {
  font-size: 0.75rem;
  color: #7e8299;
  white-space: nowrap;
}

</style>